import React, { useEffect, useState } from 'react';
import { LockIcon } from 'lucide-react';
type CheckoutCoverProps = {
  isVisible: boolean;
  /** Same cart URL already in flight, exposed only by the failsafe. */
  fallbackUrl: string;
};

/** Only if the navigation never happens do we offer a way out. */
const FAILSAFE_AFTER_MS = 8000;
export function CheckoutCover({
  isVisible,
  fallbackUrl
}: CheckoutCoverProps) {
  const [hasFailed, setHasFailed] = useState(false);
  useEffect(() => {
    if (!isVisible) {
      setHasFailed(false);
      return;
    }
    const timer = window.setTimeout(() => setHasFailed(true), FAILSAFE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [isVisible]);
  if (!isVisible) return null;
  return <div className="cover-root" role="status" aria-live="polite" data-id="element-33" data-mp="3nvh1ythl5vab">
      <p className="cover-chrome" data-id="element-34" data-mp="27ijwu1j6os89">
        <LockIcon style={{
        width: 11,
        height: 11
      }} aria-hidden="true" data-id="element-35" data-mp="27hh8a09c31vi" />
        secure checkout
      </p>

      <div className="cover-stage" data-id="element-36" data-mp="2ciy77q89ce9g">
        <div className="cover-track" data-id="element-37" data-mp="2cf0oh5pldoq3">
          <div className="cover-ground" aria-hidden="true" data-id="element-38" data-mp="3kdaqkg04lq2z" />
          <div className="cover-travel" data-id="element-39" data-mp="3ff0rhuba65c3">
            <div className="cover-shadow" aria-hidden="true" data-id="element-40" data-mp="3fg2duuxqye94" />
            <div className="cover-bob" data-id="element-41" data-mp="3uc9vbnhrt1kp">
              <WalkingRobot data-id="element-42" data-mp="3ud1iqb17rszw" />
            </div>
          </div>
        </div>

        <p className="cover-line" data-id="element-43" data-mp="3pcnktltbtzfl">
          Opening secure checkout
          <span className="cover-dots" aria-hidden="true" data-id="element-44" data-mp="3pfjgf3hpcc0v">
            <span data-id="element-45" data-mp="76963kn8hlg5" />
            <span data-id="element-46" data-mp="75hc2e9abvrz" />
            <span data-id="element-47" data-mp="28h603cfqt0v" />
          </span>
        </p>
        <p className="cover-sub" data-id="element-48" data-mp="27fr1nsatmdv">Tonight the cardboard comes out and the phone goes down.</p>

        {hasFailed ? <a href={fallbackUrl} className="cover-fallback" data-id="element-49" data-mp="gwu0swzse5ts">
            Continue to checkout
          </a> : null}
      </div>

      <p className="cover-foot" data-id="element-50" data-mp="py1qwrs5g0cs">powered by shopify</p>
    </div>;
}

/** Small cardboard robot walking: bobbing, arms and legs swinging out of phase. */
function WalkingRobot() {
  return <svg className="cover-robot" viewBox="0 0 64 72" width="64" height="72" fill="none" aria-hidden="true" data-id="element-51" data-mp="lun9bwokbbx8">
      <g className="cover-antenna" data-id="element-52" data-mp="xzstjqvtch0f">
        <line x1="32" y1="7" x2="32" y2="12" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-53" data-mp="xwn94oy4q3b1" />
        <circle cx="32" cy="4" r="3" fill="#7FBF6B" data-id="element-54" data-mp="17q43bu26hkf8" />
      </g>

      {/* head */}
      <rect x="18" y="12" width="28" height="22" rx="7" fill="#FBF7EF" stroke="#1B2C4F" strokeWidth="2.5" data-id="element-55" data-mp="1bpcselm9wu4g" />
      <circle cx="26" cy="23" r="2.2" fill="#1B2C4F" data-id="element-56" data-mp="1lo2cml1n6qnv" />
      <circle cx="38" cy="23" r="2.2" fill="#1B2C4F" data-id="element-57" data-mp="1lm97xbalagii" />
      <line x1="25" y1="29" x2="39" y2="29" stroke="#C8912B" strokeWidth="2.5" strokeLinecap="round" data-id="element-58" data-mp="1qmdot2fetd99" />

      <line x1="32" y1="34" x2="32" y2="38" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-59" data-mp="3seib9ye53icl" />

      {/* body: a cardboard box, flaps taped across */}
      <rect x="16" y="38" width="32" height="22" rx="4" fill="#FBF7EF" stroke="#1B2C4F" strokeWidth="2.5" data-id="element-60" data-mp="24sp04jo52dix" />
      <line x1="16" y1="38" x2="48" y2="60" stroke="#1B2C4F" strokeWidth="1.3" opacity="0.4" data-id="element-61" data-mp="1vsiqx9zh3zpk" />
      <line x1="48" y1="38" x2="16" y2="60" stroke="#1B2C4F" strokeWidth="1.3" opacity="0.4" data-id="element-62" data-mp="2sijg3a3r8i7e" />

      <g className="cover-arm-left" data-id="element-63" data-mp="2ogy2tnnb61wo">
        <line x1="16" y1="44" x2="6" y2="52" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-64" data-mp="2ohziy6j6lel9" />
        <circle cx="6" cy="52" r="1.8" fill="#1B2C4F" data-id="element-65" data-mp="2eja0abjgnq3y" />
      </g>
      <g className="cover-arm-right" data-id="element-66" data-mp="2el4i3gzfr23t">
        <line x1="48" y1="44" x2="58" y2="52" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-67" data-mp="182y6emno8ue6" />
        <circle cx="58" cy="52" r="1.8" fill="#1B2C4F" data-id="element-68" data-mp="2r0jb18stahxb" />
      </g>

      <g className="cover-leg-left" data-id="element-69" data-mp="2qy6n8f0dhdcc">
        <line x1="24" y1="60" x2="22" y2="70" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-70" data-mp="2vrjbgo153i4k" />
        <line x1="18" y1="70" x2="26" y2="70" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-71" data-mp="2mypijnsrsk88" />
      </g>
      <g className="cover-leg-right" data-id="element-72" data-mp="3epvju7k88nz3">
        <line x1="40" y1="60" x2="42" y2="70" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-73" data-mp="3eou800vdzxgb" />
        <line x1="38" y1="70" x2="46" y2="70" stroke="#1B2C4F" strokeWidth="2.5" strokeLinecap="round" data-id="element-74" data-mp="35onwuln5v9kb" />
      </g>
    </svg>;
}